import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { AuthApis } from "../../../api/auth/auth";
import ChangePasswordModal from "../Modals/ChangePasswordModal";

import {
  appUser,
  appToken,
  setAppToken,
  setAppUser,
} from "../../../utils/storageHelper";

export default function CreateUser() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [users, setUsers] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  /* const [deletingId, setDeletingId] = useState(null); */

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isSuperUser, setIsSuperUser] = useState(false);

  const openChangePasswordModal = (userId, superUserFlag) => {
    setSelectedUserId(userId);
    setIsSuperUser(superUserFlag);
    setShowChangePassword(true);
  };

  // functon to fetch users from API
  const fetchUsers = async () => {
    try {
      const res = await AuthApis.GetUsers();

      // ✅ use the correct property
      setUsers(res.data.users || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to fetch users";
      toast.error(msg);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ Load users once
  /* useEffect(() => {
    const stored = localStorage.getItem("users");
    if (stored) {
      setUsers(JSON.parse(stored));
    }
  }, []); */

  // ✅ Save users whenever it changes stores data in local storage
  /*   useEffect(() => {
    if (users.length > 0) {
      localStorage.setItem("users", JSON.stringify(users));
    } else {
      localStorage.removeItem("users");
    }
  }, [users]); */

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!email || !password || !companyName) {
      toast.error("Both fields are required!");
      return;
    }
    if (!isValidEmail(email)) {
      toast.error("Invalid email format!");
      return;
    }

    try {
      const response = await AuthApis.CreateUser(email, password, companyName);
      if (response.status === 200) {
        toast.success("User added successfully!");
        setEmail("");
        setPassword("");
        setCompanyName("");
        fetchUsers(); // refresh table from backend
      } else if (response.status === 400) {
        toast.error("Login failed. User Already Exists");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("Login failed. User Already Exists");
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          "Failed to add user";
        toast.error(msg);
      }
    }
  };

  const handleDeleteUser = async (id) => {
    // ask confirmation
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This user will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return; // user cancelled

    try {
      // show a loading modal while API call runs
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // call API
      const response = await AuthApis.DeleteUser(id);

      // close loading modal
      Swal.close();

      // treat 200/204 as success (adjust if your API returns something else)
      if (response?.status === 200 || response?.status === 204) {
        toast.success("User deleted successfully!");
        fetchUsers(); // refresh table from backend
      } else if (response?.status === 404) {
        toast.error("User not found or already deleted");
      } else {
        // unexpected response
        const msg = response?.data?.message || "Failed to delete user";
        toast.error(msg);
      }
    } catch (err) {
      // close loading modal in case of network / server error
      Swal.close();

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to delete user";
      toast.error(msg);
    }
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="container-fluid min-vh-100 d-flex flex-column">
      <div className="row justify-content-center flex-grow-1">
        <div className="col-12 col-lg-10 col-xl-12">
          <div className="card shadow-sm mt-3 h-100">
            <div className="card-body d-flex flex-column py-4">
              <h4 className="card-title mb-3">User Management</h4>

              {/* Form */}
              <form className="row g-3 mb-4" onSubmit={handleAddUser}>
                <div className="col-12 col-md-3">
                  <label className="form-label">
                    Enter Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* ✅ NEW COMPANY FIELD */}
                <div className="col-12 col-md-3">
                  <label className="form-label">
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12 col-md-3 position-relative">
                  <label className="form-label">
                    Enter Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control pe-md-6"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    aria-required="true"
                    onKeyDown={(e) => e.key === "Enter" && handleAddUser(e)} // if you wired Enter to submit
                  />
                  <span
                    className="input-password-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <div className="col-12 col-md-3 d-grid align-self-end">
                  <button type="submit" className="btn btn-primary">
                    Add User
                  </button>
                </div>
              </form>

              {/* Table */}
              <div className="table-responsive flex-grow-1 overflow-auto">
                <table className="table table-hover table-bordered w-100">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Password</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((u, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{u.email}</td>
                          <td>{u.company_name || "—"}</td>
                          <td>••••••••</td>
                          {/* placeholder since no password in API */}
                          <td className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-warning"
                              style={{ width: "84px" }}
                              onClick={() =>
                                openChangePasswordModal(
                                  u.id || u._id,
                                  u.is_superuser || false
                                )
                              }
                            >
                              Change Password
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() =>
                                handleDeleteUser(u._id || u.id || i)
                              }
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          No users yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ChangePasswordModal
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userId={selectedUserId}
        isSuperUser={isSuperUser}
    
      />
    </div>
  );
}
